import { WazuhAgentConnection, type SystemHealth } from '@bartizo/shared';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { getSystemHealth } from '../api/system.js';
import { formatBytes } from '../lib/format.js';
import { formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Card } from '../ui/Card.js';

/**
 * The central server itself (S8, S19; P8.3), on the overview for admins: whether
 * the database is being backed up, and whether partition maintenance is keeping
 * ahead. Either failing is otherwise silent until the day it matters, so a
 * problem is a warning above the details rather than a line inside them.
 */

const FIVE_MINUTES = 5 * 60_000;

export function SystemHealthCard({ timezone }: { timezone: string }): ReactNode {
  const health = useQuery({
    queryKey: ['system-health'],
    queryFn: getSystemHealth,
    refetchInterval: FIVE_MINUTES,
  });

  if (health.isPending) {
    return (
      <Card title="This central server">
        <p className="row muted" aria-busy="true">
          <span className="spinner" aria-hidden="true" />
          Checking…
        </p>
      </Card>
    );
  }
  if (health.isError) {
    return (
      <Card title="This central server">
        <p className="muted text-sm">Could not check its backups and partitions just now.</p>
      </Card>
    );
  }

  const { backups, partitions, wazuhAlerts, wazuhAgentConfigs, wazuhAgents } = health.data;
  const overflowing = partitions.defaults.filter((each) => each.rows > 0);
  // The reader runs every few seconds; quiet for this long, the API's job has stopped.
  const readerStopped =
    wazuhAlerts !== null && Date.now() - Date.parse(wazuhAlerts.checkedAt) > FIVE_MINUTES;

  return (
    <>
      {backups.state === 'stale' && backups.newest !== null && (
        <Alert tone="warning" title="The database has not been backed up lately">
          The newest backup finished {formatRelative(backups.newest.finishedAt)}, and one runs
          daily. On the central server, <code>systemctl status bartizo-central-backup</code> and{' '}
          <code>journalctl -u bartizo-central-backup</code> say why.
        </Alert>
      )}
      {backups.state === 'none' && (
        <Alert tone="warning" title="No backup of the database has been recorded">
          The installer scheduled a daily one. On the central server,{' '}
          <code>journalctl -u bartizo-central-backup</code> says why none has finished.
        </Alert>
      )}
      {overflowing.length > 0 && (
        <Alert tone="warning" title="Partition maintenance has fallen behind">
          Rows are arriving with no partition to hold them ({describeOverflow(overflowing)}), so
          they are neither indexed by time nor expired. The API’s log says why its partition job is
          failing; once it runs, new rows go where they belong.
        </Alert>
      )}

      {wazuhAlerts?.status === 'unreadable' && (
        <Alert tone="warning" title="The API cannot read the Wazuh manager's alerts">
          Permission was denied, so file changes on servers that report to this central server are
          not being reported. Running <code>install.sh central</code> again puts{' '}
          <code>bartizo-api</code> back in the <code>wazuh</code> group.
        </Alert>
      )}
      {wazuhAlerts?.status === 'missing' && (
        <Alert tone="warning" title="The Wazuh manager has written no alerts file">
          <code>/var/ossec/logs/alerts/alerts.json</code> does not exist; on the central server,{' '}
          <code>systemctl status wazuh-manager</code> says whether it is running.
        </Alert>
      )}
      {readerStopped && (
        <Alert tone="warning" title="The API has stopped reading Wazuh's alerts">
          Its last look was {formatRelative(wazuhAlerts.checkedAt)}. The API's log says why its{' '}
          <code>wazuh-alerts</code> job is failing.
        </Alert>
      )}

      {wazuhAgentConfigs !== null && wazuhAgentConfigs.failing.length > 0 && (
        <Alert tone="warning" title="Some servers' watched paths did not reach their Wazuh agent">
          The Wazuh manager refused the new file for{' '}
          {wazuhAgentConfigs.failing.map((each) => each.serverId).join(', ')}, so{' '}
          {wazuhAgentConfigs.failing.length === 1 ? 'it keeps' : 'they keep'} watching the last good
          paths. Each server's events say why.
        </Alert>
      )}

      {wazuhAgents?.error != null && (
        <Alert tone="warning" title="The API cannot ask the Wazuh manager how its agents are">
          {wazuhAgents.error}.{' '}
          {wazuhAgents.readAt === null
            ? 'No server’s Wazuh agent has been read yet'
            : `What servers show of their Wazuh agent is as of ${formatRelative(wazuhAgents.readAt)}`}
          , and a stopped agent would not be noticed. On the central server,{' '}
          <code>systemctl status wazuh-manager</code> says whether its API is running.
        </Alert>
      )}

      <Card title="This central server">
        <dl className="details">
          <dt>Newest backup</dt>
          <dd>
            <BackupLine backups={backups} timezone={timezone} />
          </dd>
          <dt>Partitions</dt>
          <dd>
            {overflowing.length === 0
              ? 'Kept ahead of the data, as they should be.'
              : `${String(overflowing.length)} of ${String(partitions.defaults.length)} tables overflowing`}
          </dd>
          {wazuhAlerts !== null && (
            <>
              <dt>Wazuh alerts</dt>
              <dd>
                <WazuhAlertsLine state={wazuhAlerts} />
              </dd>
            </>
          )}
          {wazuhAgentConfigs !== null && (
            <>
              <dt>Watched paths to agents</dt>
              <dd>
                <WazuhAgentConfigsLine state={wazuhAgentConfigs} />
              </dd>
            </>
          )}
          {wazuhAgents !== null && (
            <>
              <dt>Wazuh agents</dt>
              <dd>
                <WazuhAgentsLine state={wazuhAgents} />
              </dd>
            </>
          )}
        </dl>
      </Card>
    </>
  );
}

function BackupLine({
  backups,
  timezone,
}: {
  backups: SystemHealth['backups'];
  timezone: string;
}): ReactNode {
  if (backups.newest === null) {
    return backups.state === 'not_scheduled' ? (
      <span className="muted">
        None recorded. This API was not installed with <code>install.sh central</code>, which
        schedules them.
      </span>
    ) : (
      <span className="muted">None recorded yet.</span>
    );
  }
  return (
    <span title={formatInstant(backups.newest.finishedAt, { timezone })}>
      {formatRelative(backups.newest.finishedAt)} · {backups.newest.label} ·{' '}
      {formatBytes(backups.newest.bytes)}
      <span className="cell-sub">{backups.newest.file}</span>
    </span>
  );
}

const describeOverflow = (tables: SystemHealth['partitions']['defaults']): string =>
  tables
    .map((each) => `${each.table}: ${each.capped ? 'at least ' : ''}${String(each.rows)}`)
    .join(', ');

function WazuhAlertsLine({
  state,
}: {
  state: NonNullable<SystemHealth['wazuhAlerts']>;
}): ReactNode {
  const parts: string[] = [];
  if (state.status === 'ok') {
    parts.push(
      state.readAt === null ? 'Read, nothing yet' : `Read ${formatRelative(state.readAt)}`,
    );
  } else {
    parts.push(state.status === 'missing' ? 'No alerts file' : 'Cannot be read');
  }
  if (state.behindBytes > 0) parts.push(`${formatBytes(state.behindBytes)} not read yet`);
  return (
    <span>
      {parts.join(' · ')}
      {state.lost !== null && <span className="cell-sub">Could not be read: {state.lost}</span>}
      {state.unknownAgents.length > 0 && (
        <span className="cell-sub">
          Wazuh agents that are no server here, their alerts not stored:{' '}
          {state.unknownAgents.map((agent) => `${agent.name} (${String(agent.alerts)})`).join(', ')}
        </span>
      )}
    </span>
  );
}

function WazuhAgentConfigsLine({
  state,
}: {
  state: NonNullable<SystemHealth['wazuhAgentConfigs']>;
}): ReactNode {
  const parts = [`${String(state.servers)} ${state.servers === 1 ? 'server' : 'servers'}`];
  if (state.waiting > 0)
    parts.push(`${String(state.waiting)} waiting for their agent to take them`);
  if (state.failing.length > 0) parts.push(`${String(state.failing.length)} refused`);
  return (
    <span>
      {parts.join(' · ')}
      {state.checkedAt !== null && (
        <span className="cell-sub">Checked {formatRelative(state.checkedAt)}</span>
      )}
      {state.failing.map((each) => (
        <span key={each.serverId} className="cell-sub">
          {each.serverId}: {each.error}
        </span>
      ))}
    </span>
  );
}

const CONNECTION_WORDS: Readonly<Record<WazuhAgentConnection, string>> = {
  active: 'active',
  disconnected: 'disconnected',
  never_connected: 'never connected',
  pending: 'connecting',
  not_enrolled: 'not enrolled',
};

function WazuhAgentsLine({
  state,
}: {
  state: NonNullable<SystemHealth['wazuhAgents']>;
}): ReactNode {
  const counted = WazuhAgentConnection.options
    .filter((connection) => (state.connections[connection] ?? 0) > 0)
    .map(
      (connection) =>
        `${String(state.connections[connection] ?? 0)} ${CONNECTION_WORDS[connection]}`,
    );
  return (
    <>
      {counted.length === 0 ? 'No server has a Wazuh agent here yet' : counted.join(', ')}
      <span className="cell-sub">
        {state.error !== null
          ? `Could not ask the manager ${formatRelative(state.checkedAt)}`
          : state.readAt === null
            ? 'Not read yet'
            : `Read from the manager ${formatRelative(state.readAt)}`}
      </span>
    </>
  );
}
